import React, { useEffect, useImperativeHandle, useRef, useState, forwardRef } from "react";
import { Loader2 } from "lucide-react";

// Loads the Square Web Payments SDK and renders a secure card entry field.
// Exposes tokenize() via ref which returns a card nonce (source_id).
export const SquareCard = forwardRef(({ appId, locationId, environment }, ref) => {
  const cardRef = useRef(null);
  const containerRef = useRef(null);
  const [status, setStatus] = useState("loading"); // loading | ready | error
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    let cancelled = false;
    const scriptUrl =
      environment === "sandbox"
        ? "https://sandbox.web.squarecdn.com/v1/square.js"
        : "https://web.squarecdn.com/v1/square.js";

    const loadScript = () =>
      new Promise((resolve, reject) => {
        if (window.Square) return resolve();
        const existing = document.querySelector(`script[src="${scriptUrl}"]`);
        if (existing) {
          existing.addEventListener("load", resolve);
          existing.addEventListener("error", reject);
          return;
        }
        const s = document.createElement("script");
        s.src = scriptUrl;
        s.onload = resolve;
        s.onerror = reject;
        document.head.appendChild(s);
      });

    (async () => {
      try {
        await loadScript();
        if (cancelled) return;
        const payments = window.Square.payments(appId, locationId);
        const card = await payments.card();
        await card.attach(containerRef.current);
        cardRef.current = card;
        if (!cancelled) setStatus("ready");
      } catch (e) {
        console.error("Square init error", e);
        if (!cancelled) {
          setStatus("error");
          setErrorMsg("Could not load secure card form. Please refresh.");
        }
      }
    })();

    return () => {
      cancelled = true;
      if (cardRef.current) {
        try { cardRef.current.destroy(); } catch (_) {}
      }
    };
  }, [appId, locationId, environment]);

  useImperativeHandle(ref, () => ({
    tokenize: async () => {
      if (!cardRef.current) throw new Error("Card form not ready");
      const result = await cardRef.current.tokenize();
      if (result.status === "OK") return result.token;
      const msg = result.errors?.map((e) => e.message).join(", ") || "Card validation failed";
      throw new Error(msg);
    },
  }));

  return (
    <div data-testid="square-card">
      <div ref={containerRef} className="rounded-md border border-black/15 bg-white p-3" />
      {status === "loading" && (
        <p className="mt-2 flex items-center gap-2 text-sm text-[var(--text-secondary)]">
          <Loader2 className="animate-spin" size={14} /> Loading secure card form…
        </p>
      )}
      {status === "error" && <p className="mt-2 text-sm text-red-600">{errorMsg}</p>}
    </div>
  );
});

SquareCard.displayName = "SquareCard";
